import React from 'react';

import {Icon} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {isSearchMode} from 'client/components/AdvancedSearch/AdvancedSearch.utils';
import type {GuideTree} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import AccuracyBadge from 'client/generic/AccuracyBadge/AccuracyBadge';
import ActionsDropdown from 'client/generic/StudyGuideItem/Teacher/ActionsDropdown/ActionsDropdown.react';

function generateActionsDropdown(
  guideLevel: any,
  hasAccessToCreateTemplate: boolean,
  linkToQuestions: string,
  isAssessment: boolean,
  guideLevelTree: GuideTree
) {
  if (isSearchMode() || (!guideLevel.isFree() && !hasAccessToCreateTemplate)) {
    return null;
  }
  return (
    <ActionsDropdown
      link={linkToQuestions}
      buttonClassName='study-guide-actions-dropdown__btn unbutton'
      className='study-guide-actions-dropdown'
      guideLevel={guideLevel}
      showManageRestrictions={isAssessment}
      guideLevelTree={guideLevelTree}
    />
  );
}

interface GuideLevelActionsProps {
  guideLevel: any;
  guideLevelDisabled: boolean;
  hasTeacherAccess: boolean;
  hasAccessToCreateTemplate: boolean;
  isStudentWithoutAccess: boolean;
  isAssessment: boolean;
  /** `getLinkToQuestions()` plus the active search query string, if any. */
  link: string;
  guideLevelTree: GuideTree;
  metadata: React.ReactNode;
  partialCreditEnabled: boolean;
}

/**
 * Everything that renders in a guide-level row's trailing action slot: the teacher actions
 * dropdown, the student accuracy badge, or one of the two lock icons.
 *
 * The lock/unlock overrides are precedence-resolved rather than disjoint — `guideLevelDisabled`
 * wins over whatever the user type would otherwise get, and the free-theme unlock icon replaces
 * the (null) theme actions.
 */
export const GuideLevelActions = ({
  guideLevel,
  guideLevelDisabled,
  hasTeacherAccess,
  hasAccessToCreateTemplate,
  isStudentWithoutAccess,
  isAssessment,
  link,
  guideLevelTree,
  metadata,
  partialCreditEnabled
}: GuideLevelActionsProps) => {
  const actionsByUserType = hasTeacherAccess ? (
    generateActionsDropdown(
      guideLevel,
      hasAccessToCreateTemplate,
      link,
      isAssessment,
      guideLevelTree
    )
  ) : (
    <AccuracyBadge
      guideLevel={guideLevel}
      metadata={metadata}
      partialCreditEnabled={partialCreditEnabled}
    />
  );

  let guideLevelActions = guideLevel.isTheme() ? null : actionsByUserType;

  if (
    guideLevel.isTheme() &&
    guideLevel.isFree() &&
    (!sessionStore.hasValidSession() || isStudentWithoutAccess)
  ) {
    guideLevelActions = <Icon icon='lock-open' />;
  }

  return guideLevelDisabled ? <Icon icon='lock' /> : guideLevelActions;
};
